// Touch input on the game canvas (Touch input mode only, inputModeState.ts): pointer events -> TouchCueController
// (touchControls.ts) -> the same calls the mouse and the Space key make (camera look, Cue.StartStroke / Stroke /
// EndStroke), with finger px × TOUCH_GAIN (applied in TouchCueController.move). The on-screen LOCK button (components/Hud/TouchControls.tsx) calls lockDown / lockUp here.
// In Desktop mode every handler here returns at once, so the mouse/keyboard path (Events.ts) is untouched.
import Game from "./Game";
import Cue from "./Cue";
import Events from "./Events";
import GameState from "./GameState";
import {PlayerSettings} from "./settings";
import {InputModeState} from "./inputModeState";
import {TouchCueController, lookAngles, zoomDistance} from "./touchControls";

const listeners = new Set<() => void>();
const notify = () => listeners.forEach(l => l());

export const controller = new TouchCueController({
    look(dx, dy) {
        const cam = Game.Camera;
        if (!cam) return;
        const a = lookAngles(cam.horizontalAngle, cam.verticalAngle, dx, dy, PlayerSettings.get().aimSensitivity);
        cam.horizontalAngle = a.h;
        cam.verticalAngle = a.v;
    },
    startLock(t) {
        // exactly what pressing Space does (Events.onKeyPress)
        if (!Events.keysPressed.space) Cue.StartStroke(t);
        Events.keysPressed.space = true;
        notify();
    },
    endLock() {
        Events.keysPressed.space = false;
        Cue.EndStroke();
        notify();
    },
    stroke(dy, t) {
        Cue.Stroke(dy, t);
    },
});

type CoalescingPointerEvent = PointerEvent & {getCoalescedEvents?: () => PointerEvent[]};

export const TouchInput = {
    controller,
    get locked() { return controller.locked; },
    /** re-render hooks (the LOCK button's pressed look) */
    subscribe(l: () => void) { listeners.add(l); return () => { listeners.delete(l); }; },

    lockDown(id: number, t: number) { if (InputModeState.isTouch()) controller.lockDown(id, t); },
    lockUp(id: number) { controller.lockUp(id); },
    /** zoom buttons: the mouse wheel's camera distance, one step at a time (+ = further) */
    zoom(steps: number) { GameState.cameraDistance = zoomDistance(GameState.cameraDistance, steps); },
    cancelAll() { controller.cancelAll(); },

    init(canvas: HTMLElement) {
        const touch = () => InputModeState.isTouch();
        canvas.addEventListener("pointerdown", (e: PointerEvent) => {
            if (!touch()) return;
            if (e.pointerType === "mouse" && e.button !== 0) return;
            e.preventDefault();
            try { canvas.setPointerCapture(e.pointerId); } catch {}
            controller.down(e.pointerId, e.clientX, e.clientY, e.timeStamp || performance.now());
            notify();
        });
        canvas.addEventListener("pointermove", (e: CoalescingPointerEvent) => {
            if (!touch() || !controller.role(e.pointerId)) return;
            // every sample the browser has (coalesced events), with its own timestamp, as the stroke tracker expects
            const list = (e.getCoalescedEvents && e.getCoalescedEvents()) || [];
            const samples = list.length ? list : [e];
            for (const s of samples) controller.move(e.pointerId, s.clientX, s.clientY, s.timeStamp || e.timeStamp || performance.now());
        });
        const up = (e: PointerEvent) => { controller.up(e.pointerId); notify(); };
        canvas.addEventListener("pointerup", up);
        canvas.addEventListener("pointercancel", up);
        canvas.addEventListener("lostpointercapture", up);
        canvas.addEventListener("contextmenu", e => { if (touch()) e.preventDefault(); });
        // iOS Safari pinch-zoom gestures over the game
        canvas.addEventListener("gesturestart", e => { if (touch()) e.preventDefault(); });
        document.addEventListener("visibilitychange", () => { if (document.hidden) controller.cancelAll(); });
        window.addEventListener("blur", () => controller.cancelAll());
        InputModeState.subscribe(() => controller.cancelAll());
    },
};
